import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Document, Page } from 'react-pdf';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { message } from 'antd';
import {
  ArrowLeft,
  BookOpenText,
  DotsSixVertical,
  Eye,
  FilePdf,
  FilePlus,
  FileDoc,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  X,
} from '@phosphor-icons/react';

import FileMergeModal from '../../components/FileMergeModal/FileMergeModal';
import OutputPreview from './OutputPreview';
import ArticlePickerModal from './ArticlePickerModal';
import MergeProgressNotification from '../../components/FileMergeModal/MergeProgressNotification';
import { getPresignedDocumentUrl } from '../../components/Document/DocumentUtils';
import {
  createAssemblyJob,
  pollAssemblyJob,
  MAX_ASSEMBLY_ITEMS,
} from '../../service/assemblyService';
import { getDocumentDetail } from '../../service/matter';
import { fetchArticleBankPreview } from '../../service/articleBank';
import {
  fetchMatterDetail,
  fetchMatterDetailList,
  postHighlightedMatter,
} from '../../slice/MatterDetailsSlice';
import { firstSerializerError } from '../../components/FileMergeModal/FileMergeModalContainerUtils';
import { convertData } from '../../utils/RestructureToDateTree';
import {
  articleSourceRecord,
  buildAssemblyItems,
  defaultBuilderOutputName,
  describeSegmentPages,
  isArticleRecord,
  isBuilderEligibleRecord,
  isPdfRecord,
  parseRangeSpec,
  segmentPageCount,
  sourceKeyFor,
} from './builderUtils';
import {
  AddDocumentButton,
  BackButton,
  BatesCheckboxLabel,
  ColumnBody,
  ColumnHeader,
  Columns,
  CreateButton,
  DragBadge,
  DragHandleButton,
  DropIndicator,
  EmptyPanelText,
  HeaderBar,
  HeaderSpacer,
  HeaderTitle,
  OutputColumn,
  OutputDropZone,
  OutputNameInput,
  OutputSummary,
  PageWrapper,
  PagesColumn,
  PagesToolbar,
  PrimaryToolbarButton,
  RangeInput,
  SegmentButton,
  SegmentCard,
  SegmentIndex,
  SegmentInfo,
  SegmentLabelInput,
  SegmentName,
  SegmentPages,
  SourceCard,
  SourceCardAction,
  SourceCardInfo,
  SourceCardMeta,
  SourceCardName,
  SourcesColumn,
  ThumbCard,
  ThumbGrid,
  ThumbPageNumber,
  ThumbPager,
  ToolbarButton,
  ToolbarError,
  ZoomControls,
} from './PdfBuilderStyled';

// Visual page-level PDF assembly ("add exhibits to the brief"): pick source
// documents from the matter, drag individual pages / ranges / whole files
// into an ordered output, and submit one server-side assembly job (the same
// POST /documents/assemble/ contract the AI/Hermes clients use). Sources
// are never modified — the output is a brand-new document in the matter.

const THUMBS_PER_WINDOW = 64;

const newSegmentId = () =>
  (globalThis.crypto?.randomUUID && globalThis.crypto.randomUUID()) ||
  `seg-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const makeSegment = (source, pages) => ({
  id: newSegmentId(),
  sourceKey: source.key,
  sourceName: source.record?.name || 'Untitled',
  fileType: source.record?.file_type || '',
  pages: pages || null, // null = every page (Office docs are always whole)
  label: '',
  labelNumbering: false, // bates-style "Exhibit A-001" per page
});

const THUMB_WIDTH_MIN = 120;
const THUMB_WIDTH_MAX = 420;
const THUMB_WIDTH_STEP = 60;

// --------------------------------------------------------------------------
// Draggable page thumbnail
// --------------------------------------------------------------------------

const PageThumb = ({ sourceKey, pageNumber, selected, onClick, width }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `page:${sourceKey}:${pageNumber}`,
    data: { type: 'page', sourceKey, page: pageNumber },
  });
  return (
    <ThumbCard
      ref={setNodeRef}
      $selected={selected}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      onClick={onClick}
      data-testid={`page-thumb-${pageNumber}`}
      {...listeners}
      {...attributes}
    >
      <Page
        pageNumber={pageNumber}
        width={width}
        renderTextLayer={false}
        renderAnnotationLayer={false}
        loading={<div style={{ height: Math.round(width * 1.29) }} />}
      />
      <ThumbPageNumber>{pageNumber}</ThumbPageNumber>
    </ThumbCard>
  );
};

PageThumb.propTypes = {
  sourceKey: PropTypes.string.isRequired,
  pageNumber: PropTypes.number.isRequired,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  width: PropTypes.number.isRequired,
};

// --------------------------------------------------------------------------
// Sortable output segment card
// --------------------------------------------------------------------------

const SegmentRow = ({
  segment,
  index,
  numPagesBySource,
  onRemove,
  onLabelChange,
  onLabelNumberingChange,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `segment:${segment.id}`,
    data: { type: 'segment', segmentId: segment.id },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : 1,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      <SegmentCard data-testid={`output-segment-${index}`}>
        <DragHandleButton
          type="button"
          aria-label="Reorder"
          {...listeners}
          {...attributes}
        >
          <DotsSixVertical size={16} />
        </DragHandleButton>
        <SegmentIndex>{index + 1}.</SegmentIndex>
        <SegmentInfo>
          <SegmentName title={segment.sourceName}>
            {segment.sourceName}
          </SegmentName>
          <SegmentPages>
            {describeSegmentPages(segment)}
            {!segment.pages &&
              numPagesBySource[segment.sourceKey] &&
              ` (${numPagesBySource[segment.sourceKey]})`}
          </SegmentPages>
          <SegmentLabelInput
            type="text"
            placeholder='Exhibit label (optional), e.g. "Exhibit A"'
            value={segment.label}
            maxLength={100}
            onChange={(e) => onLabelChange(segment.id, e.target.value)}
          />
          {segment.label && segment.label.trim() && (
            <BatesCheckboxLabel>
              <input
                type="checkbox"
                data-testid={`segment-bates-${index}`}
                checked={!!segment.labelNumbering}
                onChange={(e) =>
                  onLabelNumberingChange(segment.id, e.target.checked)
                }
              />
              Bates-stamp pages ({segment.label.trim()}-001, -002, …)
            </BatesCheckboxLabel>
          )}
        </SegmentInfo>
        <SegmentButton
          type="button"
          aria-label="Remove from output"
          onClick={() => onRemove(segment.id)}
        >
          <X size={16} />
        </SegmentButton>
      </SegmentCard>
    </div>
  );
};

SegmentRow.propTypes = {
  segment: PropTypes.shape({
    id: PropTypes.string.isRequired,
    sourceKey: PropTypes.string.isRequired,
    sourceName: PropTypes.string,
    pages: PropTypes.arrayOf(PropTypes.number),
    label: PropTypes.string,
    labelNumbering: PropTypes.bool,
  }).isRequired,
  index: PropTypes.number.isRequired,
  numPagesBySource: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func.isRequired,
  onLabelNumberingChange: PropTypes.func.isRequired,
};

// --------------------------------------------------------------------------
// Page
// --------------------------------------------------------------------------

const PdfBuilder = () => {
  const { id: matterUrlId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { matterDetailsTree } = useSelector(
    (state) => state.matterDetails
  );

  const seedRecords = useMemo(
    () =>
      (location.state?.seedRecords || []).filter(isBuilderEligibleRecord),
    [location.state]
  );
  // Folder the user launched from — becomes the output's destination.
  const destinationFolder = location.state?.folder || undefined;

  // The URL param IS the matter's Salesforce text id (route
  // /matter/:id/pdf-builder). Only trust the store's tree id when it
  // belongs to THIS matter — during cross-matter navigation Redux holds
  // the previous matter's tree until fetchMatterDetail lands (same trap
  // as Bugbot 265/R1 on MatterDetails), which would submit the packet
  // against the wrong matter.
  const currentMatterId =
    String(matterDetailsTree?.id) === String(matterUrlId)
      ? matterDetailsTree.id
      : matterUrlId;

  // ---- sources ----
  const [sources, setSources] = useState(() =>
    seedRecords.map((record) => ({ key: sourceKeyFor(record), record }))
  );
  const [activeSourceKey, setActiveSourceKey] = useState(
    () => sources.find((s) => isPdfRecord(s.record))?.key || null
  );
  const [sourceUrls, setSourceUrls] = useState({}); // key -> {url, withCredentials}
  const [numPagesBySource, setNumPagesBySource] = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [articlePickerOpen, setArticlePickerOpen] = useState(false);

  // ---- output ----
  const [segments, setSegments] = useState(() =>
    seedRecords.map((record) =>
      makeSegment({ key: sourceKeyFor(record), record }, null)
    )
  );
  const [outputName, setOutputName] = useState(() =>
    defaultBuilderOutputName(seedRecords, new Date(), seedRecords.length > 1)
  );
  // Until the user edits the name themselves, keep the suggestion honest:
  // "(with exhibits)" only while the output holds more than one item.
  const [nameEdited, setNameEdited] = useState(false);

  // ---- page selection (active source only) ----
  const [selectedPages, setSelectedPages] = useState(() => new Set());
  const lastClickedRef = useRef(null);
  const [rangeText, setRangeText] = useState('');
  const [rangeError, setRangeError] = useState(null);
  const [thumbWindow, setThumbWindow] = useState(0);
  const [thumbWidth, setThumbWidth] = useState(THUMB_WIDTH_MIN);

  // ---- submission ----
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [outputDoc, setOutputDoc] = useState(null);

  const [dragBadge, setDragBadge] = useState(null);
  // Insertion marker between output segments while dragging (index in
  // segments; segments.length = after the last one).
  const [dropIndicator, setDropIndicator] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const sourcesByKey = useMemo(() => {
    const map = {};
    for (const source of sources) map[source.key] = source;
    return map;
  }, [sources]);

  // Live root cause of "with exhibits but no exhibits" (staging job 11,
  // 2026-07-21): the default name promised exhibits while the output held
  // a single item. Track the honest suggestion until the user renames.
  useEffect(() => {
    if (nameEdited) return;
    setOutputName(
      defaultBuilderOutputName(seedRecords, new Date(), segments.length > 1)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments.length > 1, nameEdited]);

  const activeSource = activeSourceKey ? sourcesByKey[activeSourceKey] : null;
  const activeIsPdf = activeSource ? isPdfRecord(activeSource.record) : false;
  const activeNumPages = activeSourceKey
    ? numPagesBySource[activeSourceKey] || 0
    : 0;

  // The matter tree feeds the document picker. It must be (re)fetched in
  // two cases: a direct page load (store empty), and — the subtle one —
  // right after navigating here from the matter page: MatterDetails'
  // unmount cleanup dispatches resetMatterDetails() AFTER this page's
  // mount effect already saw the still-populated tree, so a mount-only
  // check leaves the picker empty ("No items found"). Re-check whenever
  // the store tree changes; the ref stops a refetch loop in case the
  // fetched tree's id ever fails to match the URL form.
  const requestedTreeForRef = useRef(null);
  useEffect(() => {
    const treeMatches =
      String(matterDetailsTree?.id) === String(matterUrlId);
    if (!treeMatches && requestedTreeForRef.current !== matterUrlId) {
      requestedTreeForRef.current = matterUrlId;
      dispatch(fetchMatterDetail(matterUrlId));
    }
  }, [matterUrlId, matterDetailsTree, dispatch]);

  const treeData = useMemo(
    () => (matterDetailsTree ? convertData([matterDetailsTree]) : []),
    [matterDetailsTree]
  );

  // Per-key request tokens: only the LATEST resolve for a source may write.
  // getPresignedDocumentUrl falls back to a streaming URL on transient
  // failures, so an out-of-order stale response could otherwise overwrite
  // a good direct URL and reload the pages column mid-use.
  const resolveTokensRef = useRef({});
  // Articles Bank articles have no matter URL: the server hands back the PDF
  // itself, which we show through a blob URL. Those URLs never expire, so one
  // is kept per article and released when the page closes.
  const articleObjectUrlsRef = useRef({});
  useEffect(() => {
    const urls = articleObjectUrlsRef.current;
    return () => {
      Object.values(urls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const resolveSourceUrl = useCallback(async (source) => {
    if (!source) return;
    const key = source.key;
    const token = (resolveTokensRef.current[key] || 0) + 1;
    resolveTokensRef.current[key] = token;
    try {
      let resolved;
      if (isArticleRecord(source.record)) {
        if (articleObjectUrlsRef.current[key]) {
          resolved = { url: articleObjectUrlsRef.current[key] };
        } else {
          const response = await fetchArticleBankPreview(
            source.record.article_id
          );
          const blob = response?.data;
          if (!(blob instanceof Blob)) throw new Error('No article file');
          const url = URL.createObjectURL(blob);
          if (resolveTokensRef.current[key] !== token) {
            URL.revokeObjectURL(url);
            return;
          }
          articleObjectUrlsRef.current[key] = url;
          resolved = { url };
        }
      } else {
        const documentId = source.record?.unique_id || source.record?.id;
        resolved = await getPresignedDocumentUrl(documentId);
        if (resolveTokensRef.current[key] !== token) return;
      }
      setSourceUrls((prev) =>
        resolved?.url
          ? { ...prev, [key]: { ...resolved, resolvedAt: Date.now() } }
          : { ...prev, [key]: { error: true } }
      );
    } catch {
      if (resolveTokensRef.current[key] !== token) return;
      setSourceUrls((prev) => ({ ...prev, [key]: { error: true } }));
    }
  }, []);

  // Resolve a viewable URL whenever a PDF source becomes active. Presigned
  // URLs are short-lived, so resolve on each activation rather than caching.
  useEffect(() => {
    if (!activeSource || !activeIsPdf) return;
    resolveSourceUrl(activeSource);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSourceKey]);

  // Keyed off the specific entry object (stable across unrelated
  // sourceUrls updates) so react-pdf doesn't reload the active PDF when
  // some other source's URL resolves.
  const activeEntry = activeSourceKey ? sourceUrls[activeSourceKey] : null;
  const activeFile = useMemo(() => {
    if (!activeEntry?.url) return null;
    return activeEntry.withCredentials
      ? { url: activeEntry.url, withCredentials: true }
      : { url: activeEntry.url };
  }, [activeEntry]);

  // ---- source management ----

  const addSource = useCallback(
    (record) => {
      const key = sourceKeyFor(record);
      if (!key) return null;
      setSources((prev) =>
        prev.some((s) => s.key === key) ? prev : [...prev, { key, record }]
      );
      if (isPdfRecord(record)) {
        setActiveSourceKey(key);
        setSelectedPages(new Set());
        setThumbWindow(0);
        setRangeText('');
        setRangeError(null);
      }
      return key;
    },
    []
  );

  const handlePickerProceed = (item) => {
    const record = item?.record || item;
    if (!isBuilderEligibleRecord(record)) {
      message.warning('Only PDF and Office documents can be added.');
      return;
    }
    addSource(record);
    setPickerOpen(false);
  };

  const addedArticleIds = useMemo(
    () =>
      new Set(
        sources
          .filter((source) => isArticleRecord(source.record))
          .map((source) => source.record.article_id)
      ),
    [sources]
  );

  const handleArticleAdd = (article) => {
    if (!article?.can_add_to_packet) {
      message.warning(
        'Saved web pages and images cannot be added to a packet yet.'
      );
      return;
    }
    addSource(articleSourceRecord(article));
    message.success(`Added "${article.title}"`);
  };

  const activateSource = (key) => {
    if (key === activeSourceKey) return;
    setActiveSourceKey(key);
    setSelectedPages(new Set());
    setThumbWindow(0);
    setRangeText('');
    setRangeError(null);
    lastClickedRef.current = null;
  };

  // ---- output segment management ----

  const appendSegment = useCallback((source, pages, atIndex = null) => {
    setSegments((prev) => {
      const segment = makeSegment(source, pages);
      if (atIndex === null || atIndex >= prev.length) {
        return [...prev, segment];
      }
      const next = [...prev];
      next.splice(atIndex, 0, segment);
      return next;
    });
  }, []);

  const removeSegment = useCallback((segmentId) => {
    setSegments((prev) => prev.filter((s) => s.id !== segmentId));
  }, []);

  const setSegmentLabel = useCallback((segmentId, label) => {
    setSegments((prev) =>
      prev.map((s) => {
        if (s.id !== segmentId) return s;
        // Clearing the label also clears bates numbering — a new label
        // must not inherit a checkbox the user confirmed for OLD text.
        if (!label.trim()) {
          return { ...s, label, labelNumbering: false };
        }
        return { ...s, label };
      })
    );
  }, []);

  const setSegmentLabelNumbering = useCallback((segmentId, labelNumbering) => {
    setSegments((prev) =>
      prev.map((s) => (s.id === segmentId ? { ...s, labelNumbering } : s))
    );
  }, []);

  // ---- page selection ----

  const handleThumbClick = (event, pageNumber) => {
    setRangeError(null);
    setSelectedPages((prev) => {
      const next = new Set(prev);
      if (event.shiftKey && lastClickedRef.current) {
        const from = Math.min(lastClickedRef.current, pageNumber);
        const to = Math.max(lastClickedRef.current, pageNumber);
        for (let page = from; page <= to; page += 1) next.add(page);
      } else if (next.has(pageNumber)) {
        next.delete(pageNumber);
      } else {
        next.add(pageNumber);
      }
      return next;
    });
    lastClickedRef.current = pageNumber;
  };

  const selectedAscending = useMemo(
    () => [...selectedPages].sort((a, b) => a - b),
    [selectedPages]
  );

  const addSelectedToOutput = (atIndex = null) => {
    if (!activeSource || selectedAscending.length === 0) return;
    appendSegment(activeSource, selectedAscending, atIndex);
    setSelectedPages(new Set());
  };

  const addRangeToOutput = () => {
    if (!activeSource) return;
    const { pages, error } = parseRangeSpec(rangeText, activeNumPages);
    if (error) {
      setRangeError(error);
      return;
    }
    setRangeError(null);
    appendSegment(activeSource, pages);
    setRangeText('');
  };

  const addWholeSourceToOutput = (source) => {
    appendSegment(source, null);
  };

  // ---- drag and drop ----

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Cross-container drags (page thumb -> output list) land where the
  // pointer actually is; closestCenter alone would snap to whatever
  // droppable center is nearest, which feels wrong across columns.
  // Sortable reordering still falls back to closestCenter.
  const collisionDetection = useCallback((args) => {
    const within = pointerWithin(args);
    return within.length ? within : closestCenter(args);
  }, []);

  const describeDrag = (data) => {
    if (data?.type === 'page') {
      const dragged =
        selectedPages.has(data.page) && selectedPages.size > 1
          ? selectedPages.size
          : 1;
      return dragged === 1
        ? `Page ${data.page}`
        : `${dragged} selected pages`;
    }
    if (data?.type === 'source') {
      const source = sourcesByKey[data.sourceKey];
      return source?.record?.name || 'Document';
    }
    return null;
  };

  const handleDragStart = (event) => {
    setDragBadge(describeDrag(event.active?.data?.current));
  };

  // Final pointer position for a drag event: the activating pointer event
  // plus the total drag delta (PointerSensor guarantees clientX/Y exist).
  const dragPointerY = (event) =>
    (event.activatorEvent?.clientY ?? 0) + (event.delta?.y ?? 0);

  /**
   * Insertion index for a page/source drop. Hovering a segment's TOP half
   * inserts before it, the BOTTOM half after it — so dropping "between two
   * exhibits" works from either neighbor, and dropping onto the lower half
   * of the last card genuinely appends (previously everything landed
   * before the hovered card, so end-drops came out second-to-last).
   */
  const dropIndexFromOver = (over, event) => {
    if (!over) return null;
    const overId = String(over.id);
    if (overId === 'output-dropzone') return segments.length;
    if (overId.startsWith('segment:')) {
      const segmentId = overId.slice('segment:'.length);
      const index = segments.findIndex((s) => s.id === segmentId);
      if (index === -1) return segments.length;
      const rect = over.rect;
      if (rect && event) {
        const below = dragPointerY(event) > rect.top + rect.height / 2;
        return index + (below ? 1 : 0);
      }
      return index;
    }
    return null;
  };

  // Live insertion marker while dragging pages/sources over the output.
  const handleDragOver = (event) => {
    const type = event.active?.data?.current?.type;
    if (type !== 'page' && type !== 'source') {
      setDropIndicator(null);
      return;
    }
    setDropIndicator(dropIndexFromOver(event.over, event));
  };

  const handleDragEnd = (event) => {
    setDragBadge(null);
    setDropIndicator(null);
    const { active, over } = event;
    if (!over) return;
    const data = active?.data?.current;

    if (data?.type === 'segment') {
      const activeIndex = segments.findIndex((s) => s.id === data.segmentId);
      const overId = String(over.id);
      if (!overId.startsWith('segment:')) return;
      const overIndex = segments.findIndex(
        (s) => s.id === overId.slice('segment:'.length)
      );
      if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
        setSegments((prev) => arrayMove(prev, activeIndex, overIndex));
      }
      return;
    }

    const dropIndex = dropIndexFromOver(over, event);
    if (dropIndex === null) return;

    if (data?.type === 'page') {
      const source = sourcesByKey[data.sourceKey];
      if (!source) return;
      const pages =
        selectedPages.has(data.page) && selectedPages.size > 1
          ? selectedAscending
          : [data.page];
      appendSegment(source, pages, dropIndex);
      setSelectedPages(new Set());
      return;
    }

    if (data?.type === 'source') {
      const source = sourcesByKey[data.sourceKey];
      if (source) appendSegment(source, null, dropIndex);
    }
  };

  // ---- submission ----

  const totalKnownPages = useMemo(() => {
    let total = 0;
    let hasUnknown = false;
    for (const segment of segments) {
      const count = segmentPageCount(
        segment,
        numPagesBySource[segment.sourceKey]
      );
      if (count > 0) total += count;
      else hasUnknown = true;
    }
    return { total, hasUnknown };
  }, [segments, numPagesBySource]);

  const handleCreate = async () => {
    // The button disables on re-render, but a double-click can land before
    // React applies the state update — guard so only one job is queued.
    if (isSubmitting) return;
    const { items, error } = buildAssemblyItems(segments, sourcesByKey);
    if (error) {
      setSubmitError(error);
      return;
    }
    if (items.length > MAX_ASSEMBLY_ITEMS) {
      setSubmitError(
        `This build needs ${items.length} assembly items — the limit is ` +
          `${MAX_ASSEMBLY_ITEMS}. Combine some page ranges and try again.`
      );
      return;
    }
    const trimmedName = (outputName || '').trim();
    const finalName = trimmedName
      ? trimmedName.toLowerCase().endsWith('.pdf')
        ? trimmedName
        : `${trimmedName}.pdf`
      : defaultBuilderOutputName(seedRecords, new Date(), segments.length > 1);

    setIsSubmitting(true);
    setIsComplete(false);
    setSubmitError(null);
    setProgress(5);
    try {
      const { job_id: jobId } = await createAssemblyJob({
        matter: currentMatterId,
        outputName: finalName,
        folder: destinationFolder,
        items,
      });
      let simulated = 15;
      const job = await pollAssemblyJob(jobId, {
        onTick: (tick) => {
          if (tick.status === 'queued') setProgress(15);
          else if (tick.status === 'running') {
            simulated = Math.min(simulated + 7, 90);
            setProgress(simulated);
          }
        },
      });
      if (job.status === 'failed') {
        throw new Error(job.error_detail || 'Failed to assemble the PDF');
      }
      const detail = await getDocumentDetail(job.output_document_id);
      const document = detail?.data;
      // Same dual refresh as the merge flow, so the matter view is current
      // whichever way the user returns to it.
      dispatch(fetchMatterDetail(currentMatterId));
      dispatch(
        fetchMatterDetailList({
          id: currentMatterId,
          page: 1,
          ...(destinationFolder ? { folder: destinationFolder } : {}),
        })
      );
      if (!document?.relative_path) {
        throw new Error(
          'The PDF was created but could not be loaded — refresh the matter to find it.'
        );
      }
      setOutputDoc(document);
      setProgress(100);
      setIsComplete(true);
      setIsSubmitting(false);
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        firstSerializerError(error?.response?.data) ||
        error?.message ||
        'Failed to assemble the PDF';
      setSubmitError(message);
      setIsSubmitting(false);
      setProgress(0);
    }
  };

  // Opening the preview needs a viewable URL for every PDF source in the
  // output — sources added whole (never activated) won't have one yet,
  // and presigned URLs are short-lived, so anything resolved a while ago
  // gets refreshed too (a long builder session must not reopen the
  // preview onto expired links). Fresh URLs are kept as-is so reopening
  // doesn't force react-pdf to reload every document; the per-key
  // resolve tokens make concurrent refreshes safe.
  const URL_REFRESH_AGE_MS = 10 * 60 * 1000;
  const handleOpenPreview = () => {
    const seen = new Set();
    for (const segment of segments) {
      if (seen.has(segment.sourceKey)) continue;
      seen.add(segment.sourceKey);
      const source = sourcesByKey[segment.sourceKey];
      if (!source || !isPdfRecord(source.record)) continue;
      const entry = sourceUrls[segment.sourceKey];
      const fresh =
        entry?.url &&
        entry.resolvedAt &&
        Date.now() - entry.resolvedAt < URL_REFRESH_AGE_MS;
      if (!fresh) {
        resolveSourceUrl(source);
      }
    }
    setPreviewOpen(true);
  };

  const handlePreviewNumPages = useCallback((sourceKey, total) => {
    setNumPagesBySource((prev) =>
      prev[sourceKey] === total ? prev : { ...prev, [sourceKey]: total }
    );
  }, []);

  const handleOpenOutput = () => {
    if (!outputDoc?.relative_path) return;
    const userId = Number(localStorage.getItem('user_id'));
    const payload = {
      matterId: outputDoc.matter,
      docId: outputDoc.unique_id?.substring(1),
      docType: 'document',
      tabAction: {
        usr_id: userId,
        document_id: String(outputDoc.unique_id?.substring(1)),
        matter_id: String(outputDoc.matter),
        action: 'open',
      },
    };
    dispatch(postHighlightedMatter(payload)).then(() => {
      navigate(`/matter${outputDoc.relative_path}`);
    });
  };

  const closeNotification = () => {
    // Dismissing while in flight stops the toast, not the job — it keeps
    // running server-side, same contract as the merge notification.
    setIsSubmitting(false);
    setIsComplete(false);
    setSubmitError(null);
    setProgress(0);
  };

  // ---- thumbnails window ----

  const windowStart = thumbWindow * THUMBS_PER_WINDOW + 1;
  const windowEnd = Math.min(
    windowStart + THUMBS_PER_WINDOW - 1,
    activeNumPages
  );
  const windowCount = activeNumPages
    ? Math.ceil(activeNumPages / THUMBS_PER_WINDOW)
    : 0;

  const jumpToPage = (value) => {
    const page = parseInt(value, 10);
    if (!page || page < 1 || page > activeNumPages) return;
    setThumbWindow(Math.floor((page - 1) / THUMBS_PER_WINDOW));
  };

  const { setNodeRef: setDropZoneRef, isOver: isOverDropZone } = useDroppable({
    id: 'output-dropzone',
  });

  return (
    <PageWrapper>
      <HeaderBar>
        <BackButton
          type="button"
          onClick={() => navigate(`/matter/${matterUrlId}`)}
        >
          <ArrowLeft size={16} />
          Back to matter
        </BackButton>
        <HeaderTitle>PDF Page Builder</HeaderTitle>
        <HeaderSpacer />
        <OutputNameInput
          type="text"
          data-testid="builder-output-name"
          placeholder="Output file name, e.g. Smith Appeal Packet.pdf"
          value={outputName}
          maxLength={200}
          onChange={(e) => {
            setNameEdited(true);
            setOutputName(e.target.value);
          }}
        />
        <CreateButton
          type="button"
          data-testid="builder-create-button"
          disabled={segments.length === 0 || isSubmitting}
          onClick={handleCreate}
        >
          <FilePlus size={16} />
          Create PDF
        </CreateButton>
      </HeaderBar>

      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={() => {
          setDragBadge(null);
          setDropIndicator(null);
        }}
      >
        <Columns>
          <SourcesColumn $tinted>
            <ColumnHeader>Source documents</ColumnHeader>
            <ColumnBody>
              {sources.map((source) => {
                const pdf = isPdfRecord(source.record);
                return (
                  <SourceCardDraggable
                    key={source.key}
                    source={source}
                    active={source.key === activeSourceKey}
                    numPages={numPagesBySource[source.key]}
                    isPdf={pdf}
                    isArticle={isArticleRecord(source.record)}
                    // Office sources activate too — the pages panel then
                    // explains they can only be added whole.
                    onActivate={() => activateSource(source.key)}
                    onAddWhole={() => addWholeSourceToOutput(source)}
                  />
                );
              })}
              <AddDocumentButton
                type="button"
                data-testid="builder-add-document"
                onClick={() => setPickerOpen(true)}
              >
                <FilePlus size={16} />
                Add document
              </AddDocumentButton>
              <AddDocumentButton
                type="button"
                data-testid="builder-add-article"
                onClick={() => setArticlePickerOpen(true)}
              >
                <BookOpenText size={16} />
                Add article
              </AddDocumentButton>
            </ColumnBody>
          </SourcesColumn>

          <PagesColumn>
            <ColumnHeader>
              {activeSource
                ? `Pages — ${activeSource.record?.name || ''}`
                : 'Pages'}
            </ColumnHeader>
            {activeSource && activeIsPdf && (
              <PagesToolbar>
                <RangeInput
                  type="text"
                  data-testid="builder-range-input"
                  placeholder="Page range, e.g. 4-7, 13-20, 51-60"
                  value={rangeText}
                  onChange={(e) => {
                    setRangeText(e.target.value);
                    setRangeError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addRangeToOutput();
                  }}
                />
                <PrimaryToolbarButton
                  type="button"
                  data-testid="builder-add-range"
                  disabled={!rangeText.trim()}
                  onClick={addRangeToOutput}
                >
                  Add range →
                </PrimaryToolbarButton>
                <PrimaryToolbarButton
                  type="button"
                  data-testid="builder-add-selected"
                  disabled={selectedPages.size === 0}
                  onClick={() => addSelectedToOutput()}
                >
                  Add selected ({selectedPages.size}) →
                </PrimaryToolbarButton>
                <ToolbarButton
                  type="button"
                  onClick={() => addWholeSourceToOutput(activeSource)}
                >
                  Add all pages →
                </ToolbarButton>
                <ToolbarButton
                  type="button"
                  disabled={selectedPages.size === 0}
                  onClick={() => setSelectedPages(new Set())}
                >
                  Clear selection
                </ToolbarButton>
                {rangeError && (
                  <ToolbarError data-testid="builder-range-error">
                    {rangeError}
                  </ToolbarError>
                )}
                <ZoomControls>
                  <ToolbarButton
                    type="button"
                    aria-label="Zoom out"
                    data-testid="builder-zoom-out"
                    disabled={thumbWidth <= THUMB_WIDTH_MIN}
                    onClick={() =>
                      setThumbWidth((w) =>
                        Math.max(THUMB_WIDTH_MIN, w - THUMB_WIDTH_STEP)
                      )
                    }
                  >
                    <MagnifyingGlassMinus size={14} />
                  </ToolbarButton>
                  <ToolbarButton
                    type="button"
                    aria-label="Zoom in"
                    data-testid="builder-zoom-in"
                    disabled={thumbWidth >= THUMB_WIDTH_MAX}
                    onClick={() =>
                      setThumbWidth((w) =>
                        Math.min(THUMB_WIDTH_MAX, w + THUMB_WIDTH_STEP)
                      )
                    }
                  >
                    <MagnifyingGlassPlus size={14} />
                  </ToolbarButton>
                </ZoomControls>
              </PagesToolbar>
            )}
            <ColumnBody>
              {!activeSource && (
                <EmptyPanelText>
                  <FilePdf size={32} />
                  Select a PDF on the left to browse its pages, or add a
                  document to get started.
                </EmptyPanelText>
              )}
              {activeSource && !activeIsPdf && (
                <EmptyPanelText>
                  <FileDoc size={32} />
                  {activeSource.record?.name} is an Office document — it will
                  be converted to PDF during assembly and can only be added
                  whole.
                  <ToolbarButton
                    type="button"
                    onClick={() => addWholeSourceToOutput(activeSource)}
                  >
                    Add whole document →
                  </ToolbarButton>
                </EmptyPanelText>
              )}
              {activeSource && activeIsPdf && (
                <>
                  {sourceUrls[activeSourceKey]?.error && (
                    <EmptyPanelText>
                      This document could not be loaded for preview. You can
                      still add pages by range above once it loads, or try
                      re-selecting it.
                    </EmptyPanelText>
                  )}
                  {activeFile && (
                    <Document
                      file={activeFile}
                      onLoadSuccess={({ numPages }) => {
                        setNumPagesBySource((prev) => ({
                          ...prev,
                          [activeSourceKey]: numPages,
                        }));
                      }}
                      onLoadError={() =>
                        setSourceUrls((prev) => ({
                          ...prev,
                          [activeSourceKey]: { error: true },
                        }))
                      }
                      loading={<EmptyPanelText>Loading pages…</EmptyPanelText>}
                      error={
                        <EmptyPanelText>
                          Could not render this PDF.
                        </EmptyPanelText>
                      }
                    >
                      {activeNumPages > 0 && (
                        <>
                          <ThumbGrid $thumbWidth={thumbWidth}>
                            {Array.from(
                              { length: windowEnd - windowStart + 1 },
                              (_, offset) => windowStart + offset
                            ).map((pageNumber) => (
                              <PageThumb
                                key={`${activeSourceKey}-${pageNumber}`}
                                sourceKey={activeSourceKey}
                                pageNumber={pageNumber}
                                selected={selectedPages.has(pageNumber)}
                                onClick={(e) =>
                                  handleThumbClick(e, pageNumber)
                                }
                                width={thumbWidth}
                              />
                            ))}
                          </ThumbGrid>
                          {windowCount > 1 && (
                            <ThumbPager>
                              <ToolbarButton
                                type="button"
                                disabled={thumbWindow === 0}
                                onClick={() =>
                                  setThumbWindow((w) => Math.max(0, w - 1))
                                }
                              >
                                ← Prev
                              </ToolbarButton>
                              <span>
                                Pages {windowStart}–{windowEnd} of{' '}
                                {activeNumPages}
                              </span>
                              <ToolbarButton
                                type="button"
                                disabled={thumbWindow >= windowCount - 1}
                                onClick={() =>
                                  setThumbWindow((w) =>
                                    Math.min(windowCount - 1, w + 1)
                                  )
                                }
                              >
                                Next →
                              </ToolbarButton>
                              <RangeInput
                                type="number"
                                style={{ width: 90 }}
                                placeholder="Go to…"
                                min={1}
                                max={activeNumPages}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    jumpToPage(e.target.value);
                                  }
                                }}
                              />
                            </ThumbPager>
                          )}
                        </>
                      )}
                    </Document>
                  )}
                </>
              )}
            </ColumnBody>
          </PagesColumn>

          <OutputColumn $tinted>
            <ColumnHeader>
              Output document
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontWeight: 400,
                  color: '#8a97a8',
                }}
              >
                {segments.length} item{segments.length === 1 ? '' : 's'}
                <ToolbarButton
                  type="button"
                  data-testid="builder-preview-button"
                  disabled={segments.length === 0}
                  onClick={handleOpenPreview}
                >
                  <Eye size={14} style={{ verticalAlign: '-2px' }} /> Preview
                </ToolbarButton>
              </span>
            </ColumnHeader>
            <ColumnBody>
              <OutputDropZone
                ref={setDropZoneRef}
                $isOver={isOverDropZone}
                data-testid="builder-output-dropzone"
              >
                {segments.length === 0 && (
                  <EmptyPanelText>
                    Drag pages or documents here — or use the “Add” buttons.
                    The output is assembled top to bottom.
                  </EmptyPanelText>
                )}
                <SortableContext
                  items={segments.map((s) => `segment:${s.id}`)}
                  strategy={verticalListSortingStrategy}
                >
                  {segments.map((segment, index) => (
                    <React.Fragment key={segment.id}>
                      {dropIndicator === index && (
                        <DropIndicator data-testid="builder-drop-indicator" />
                      )}
                      <SegmentRow
                        segment={segment}
                        index={index}
                        numPagesBySource={numPagesBySource}
                        onRemove={removeSegment}
                        onLabelChange={setSegmentLabel}
                        onLabelNumberingChange={setSegmentLabelNumbering}
                      />
                    </React.Fragment>
                  ))}
                  {dropIndicator === segments.length &&
                    segments.length > 0 && (
                      <DropIndicator data-testid="builder-drop-indicator" />
                    )}
                </SortableContext>
              </OutputDropZone>
            </ColumnBody>
            <OutputSummary>
              {totalKnownPages.total > 0 &&
                `≈ ${totalKnownPages.total} page${
                  totalKnownPages.total === 1 ? '' : 's'
                }${totalKnownPages.hasUnknown ? ' + full documents' : ''} · `}
              Saved as a new document{' '}
              {destinationFolder
                ? 'in the folder you launched from'
                : 'at the matter root'}
              . Source files are never modified.
            </OutputSummary>
          </OutputColumn>
        </Columns>

        <DragOverlay dropAnimation={null}>
          {dragBadge ? <DragBadge>{dragBadge}</DragBadge> : null}
        </DragOverlay>
      </DndContext>

      <FileMergeModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title="Add a document to the builder"
        treeData={treeData}
        selectedFiles={[]}
        onProceed={handlePickerProceed}
        onCancel={() => setPickerOpen(false)}
      />

      <ArticlePickerModal
        open={articlePickerOpen}
        onClose={() => setArticlePickerOpen(false)}
        onAdd={handleArticleAdd}
        addedArticleIds={addedArticleIds}
      />

      {previewOpen && (
        <OutputPreview
          segments={segments}
          sourcesByKey={sourcesByKey}
          sourceUrls={sourceUrls}
          numPagesBySource={numPagesBySource}
          onNumPages={handlePreviewNumPages}
          outputName={outputName}
          onClose={() => setPreviewOpen(false)}
          onCreate={() => {
            setPreviewOpen(false);
            handleCreate();
          }}
          isSubmitting={isSubmitting}
        />
      )}

      {(isSubmitting || isComplete || submitError) && (
        <MergeProgressNotification
          progress={progress}
          isComplete={isComplete}
          mergedFileUrl={outputDoc?.relative_path || null}
          error={submitError}
          onClose={closeNotification}
          onOpenFile={handleOpenOutput}
          inProgressLabel="Assembling PDF."
          successLabel="PDF created"
        />
      )}
    </PageWrapper>
  );
};

// Draggable wrapper for a source card (dragging the card adds the whole
// document to the output).
const SourceCardDraggable = ({
  source,
  active,
  numPages,
  isPdf,
  isArticle,
  onActivate,
  onAddWhole,
}) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `source:${source.key}`,
    data: { type: 'source', sourceKey: source.key },
  });
  return (
    <SourceCard
      ref={setNodeRef}
      $active={active}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      onClick={onActivate}
      data-testid={`builder-source-${source.key}`}
      {...listeners}
      {...attributes}
    >
      {isArticle ? (
        <BookOpenText size={20} color="#0c53bb" />
      ) : isPdf ? (
        <FilePdf size={20} color="#c0392b" />
      ) : (
        <FileDoc size={20} color="#0c53bb" />
      )}
      <SourceCardInfo>
        <SourceCardName title={source.record?.name}>
          {source.record?.name || 'Untitled'}
        </SourceCardName>
        <SourceCardMeta>
          {isArticle
            ? numPages
              ? `Article — ${numPages} pages`
              : 'Article'
            : isPdf
              ? numPages
                ? `${numPages} pages`
                : 'PDF'
              : 'Office — converts to PDF'}
        </SourceCardMeta>
      </SourceCardInfo>
      <SourceCardAction
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onAddWhole();
        }}
      >
        Add all →
      </SourceCardAction>
    </SourceCard>
  );
};

SourceCardDraggable.propTypes = {
  source: PropTypes.shape({
    key: PropTypes.string.isRequired,
    record: PropTypes.object,
  }).isRequired,
  active: PropTypes.bool,
  numPages: PropTypes.number,
  isPdf: PropTypes.bool,
  isArticle: PropTypes.bool,
  onActivate: PropTypes.func.isRequired,
  onAddWhole: PropTypes.func.isRequired,
};

export default PdfBuilder;
